import {
	EstablishProcessRequest,
	Process,
	ProcessIndexPage,
	SharedData,
	SpecifyProcessDetailsRequest,
	Variable,
} from '@/scripts/types/generated';
import {Page} from '@inertiajs/core';
import {sendUpdateRequest} from '@/scripts/utils/crud-requests.ts';

const processes = ref<Process[]>();
const isLoading = ref<boolean>(false);

export const useProcesses = () => {
	const {getGraphVariables} = useProcess();

	const refreshProcesses = (page: Page<ProcessIndexPage | SharedData>) => {
		setProcesses(page.props.processes);
	};

	const initProcesses = async () => {
		while (isLoading.value) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		if (processes.value) return;

		isLoading.value = true;
		const page = await sendReloadRequest<SharedData>(['processes']);
		refreshProcesses(page);
		isLoading.value = false;
	};

	const setProcesses = (newProcesses: Process[]) => {
		processes.value = newProcesses;
	};

	const createProcess = async (
		request: EstablishProcessRequest
	): Promise<Process> => {
		const page = await sendCreateRequest<ProcessIndexPage>(
			route('tenant.processes.store'),
			request,
			['processes']
		);

		return new Promise<Process>((resolve) => {
			refreshProcesses(page);
			const newProcess = processes.value.find(
				(process) => process.name === request.name
			);
			resolve(newProcess);
		});
	};

	const updateProcessMetaInfo = async (
		slug: string,
		request: SpecifyProcessDetailsRequest
	) => {
		const page = await sendUpdateRequest<ProcessIndexPage>(
			route('tenant.processes.update', {
				process: slug,
			}),
			request,
			['processes']
		);

		return new Promise<Process>((resolve) => {
			refreshProcesses(page);
			const updatedProcess = processes.value.find(
				(process) => process.uuid === request.processUuid
			);
			resolve(updatedProcess);
		});
	};

	const deleteProcess = async (slug: string): Promise<void> => {
		const page = await sendDeleteRequest<ProcessIndexPage>(
			route('tenant.processes.destroy', {
				process: slug,
			}),
			['processes']
		);

		return new Promise<void>((resolve) => {
			refreshProcesses(page);
			resolve();
		});
	};

	const getProcessVariables = async (
		process: Process
	): Promise<Variable[]> => {
		return await getGraphVariables(process.graph);
	};

	return {
		processes,
		initProcesses,
		setProcesses,
		createProcess,
		updateProcessMetaInfo,
		deleteProcess,
		getProcessVariables,
	};
};
